/**
 * Error-diagnosis helpers for the lesson player. The Mux player only surfaces
 * a generic "Network Error" overlay, so on failure we (1) beacon the real
 * MediaError to our own first-party endpoint (Mux only logs requests it
 * receives, and PostHog beacons are commonly ad-blocked) and (2) ask Mux
 * directly what it rejected (CORS is `*`) so the player can pick the right
 * message — or recover — for the learner.
 */

/**
 * The shape Mux Player attaches to the `error` event's `detail`: a MediaError
 * (from @mux/playback-core) carrying the standard HTMLMediaElement `code` plus
 * Mux's fine-grained `muxCode` and `errorCategory`. We only read a few fields,
 * so we describe them structurally instead of importing the class.
 */
export interface MuxPlaybackError {
  code?: number
  muxCode?: number
  errorCategory?: string
  message?: string
  fatal?: boolean
}

/**
 * Report a playback failure to our own same-origin endpoint. Best-effort: an
 * error reporter must never throw from inside the player's own error handler.
 */
export function reportPlaybackError(payload: Record<string, unknown>): void {
  try {
    const body = new Blob([JSON.stringify(payload)], { type: 'application/json' })
    navigator.sendBeacon('/api/mux/playback-error', body)
  }
  catch {
    // Swallow deliberately: telemetry failure must not mask the original
    // playback error or break the error-handling UX.
  }
}

export type ManifestDiagnosis
  = | { kind: 'reachable' }
    | { kind: 'rejected', status: number, body: string }
    | { kind: 'unreachable', error: unknown }

/**
 * Probe the HLS manifest with the current token. Distinguishes "the stream is
 * fine, the player just choked" (recoverable by remounting) from "Mux rejected
 * us" (403 signing mismatch / 404 gone) and "network is down".
 */
export async function diagnoseManifest(
  playbackId: string,
  token: string,
): Promise<ManifestDiagnosis> {
  const manifestUrl = `https://stream.mux.com/${playbackId}.m3u8?token=${token}`
  try {
    const res = await fetch(manifestUrl, { cache: 'no-store' })
    if (res.ok)
      return { kind: 'reachable' }
    const body = await res.text().catch(() => '')
    return { kind: 'rejected', status: res.status, body: body.slice(0, 300) }
  }
  catch (error) {
    return { kind: 'unreachable', error }
  }
}
